import { omitBy, isUndefined, isPlainObject, isEmpty } from 'lodash';
import queryString from 'query-string';

import { api } from '@xtnd/web/shared/api';
import { QueryStringParams, PaginatedResponse, ApiResponse } from '@xtnd/web/shared/types';
import { UpdateWriteOffPayload, WRITE_OFF_SORT_BY, WriteOff, WriteOffItem } from '../types/writeOffs';

class ReportingWriteOffsApi {
    private readonly baseResource = '/ar-collections';
    private readonly baseService = '/writeoffs';

    /**
     * Retrieves paginated write-offs with filtering, sorting, and pagination options.
     *
     * @param params - Query string parameters including filters and pagination metadata.
     * @returns Paginated write-off list with metadata.
     */
    async getWriteOffs(params: QueryStringParams): Promise<PaginatedResponse<WriteOff>> {
        const queryParams: Record<string, any> = {
            filters: params.filters || {},
            limit: params.limit,
            page: params.page,
        };

        const sorts = params.sorts as Record<WRITE_OFF_SORT_BY, 'asc' | 'desc'>;

        // sorting by 1 field
        if (sorts) {
            const values = (Object.entries(sorts) as [WRITE_OFF_SORT_BY, 'asc' | 'desc' | undefined][])
                .find(([, order]) => order !== undefined);

            if (values) {
                const [sortBy, order] = values;
                queryParams.sortBy = sortBy;
                queryParams.order = order;
            }
        }

        const paramsCleaned = omitBy(
            queryParams,
            value => isUndefined(value) || (isPlainObject(value) && isEmpty(value))
        );

        const query = queryString.stringify(
            {
                ...paramsCleaned,
                filters: paramsCleaned.filters ? JSON.stringify(paramsCleaned.filters) : undefined,
            },
            { arrayFormat: 'comma' }
        );

        let endpoint = `${this.baseResource}${this.baseService}`;
        if (query) {
            endpoint = `${endpoint}?${query}`;
        }

        return await api.get<PaginatedResponse<WriteOff>>(endpoint);
    }

    /**
     * Retrieves lightweight write-off items by invoice ID.
     *
     * @param invoiceId - Invoice ID.
     * @returns Write-off items.
     */
    async getWriteOffsItems(invoiceId: string): Promise<PaginatedResponse<WriteOffItem>> {
        const endpoint = `${this.baseResource}/invoices/${invoiceId}/writeoffs`;

        return await api.get<PaginatedResponse<WriteOffItem>>(endpoint);
    }

    async patchWriteOff(data: UpdateWriteOffPayload, isAdmin: boolean) {
        if (!isAdmin) {
            throw new Error('Only administrators can modify write-offs.');
        }
        const endpoint = `${this.baseResource}${this.baseService}/${data.writeOffId}`;
        const { writeOffId, ...payload } = data
        return await api.patch<ApiResponse<WriteOffItem>>(endpoint, payload);
    }

    /**
     * Retrieves a single write-off by ID.
     *
     * @param writeOffId - Write-off ID.
     * @returns Single write-off item.
     */
    async getWriteOff(writeOffId: string): Promise<ApiResponse<WriteOffItem>> {
        const endpoint = `${this.baseResource}${this.baseService}/${writeOffId}`;
        return await api.get<ApiResponse<WriteOffItem>>(endpoint);
    }

    /**
     * Creates a pending CUTS_APPLIED write-off for an invoice.
     */
    async createCutsAppliedWriteOff(
        invoiceId: string,
        appealId: string,
        payload: { feesCut: number; expensesCut: number; comment?: string }
    ): Promise<ApiResponse<WriteOffItem>> {
        const endpoint = `${this.baseResource}/invoices/${invoiceId}/appeal/${appealId}/writeoffs`;
        return await api.post<ApiResponse<WriteOffItem>>(endpoint, payload);
    }
}

export const reportingWriteOffsApi = new ReportingWriteOffsApi();
